import {
    ConfigViewType,
    DesignErrorKey,
    ItemListView,
    NodeTypeName,
    ObjectClass,
    VariableType
} from "@app/types";
import { safeParseInt } from "@app/utilities";
import BaseNodeModel from "../BaseNodeModel";
import { NoInputTimeoutOverride } from "../../form_components";
import { ObjectDropdown, ValueOrVariableAutocomplete } from "../../page-components";

/**
 * JSDoc imports.
 *
 * @import { InputObjectConfig } from '@app/types';
 */

/**
 * @typedef CreateNumberConfigViewProps
 * @type {object}
 * @property {string} label
 * @property {string} name
 * @property {string} tooltip
 */

/** @extends BaseNodeModel<InputObjectConfig> */
export default class InputNodeModel extends BaseNodeModel {
    /**
     * Input node model
     * @module InputNodeModel
     * @param {string} nodeName Node display name
     * @param {number} x Node x coordinate on canvas
     * @param {number} y Node y coordinate on canvas
     * @param {Object} props Pre-defined node properties; if non-null, apply values to node properties
     * @param {string} id If non-null, use this ID as the node ID
     */
    constructor(
        nodeName = "UNTITLED_NODE",
        x = 0,
        y = 0,
        props = null,
        id = null
    ) {
        super(nodeName, x, y, null, id);
        this.props.nodeTypeName = NodeTypeName.input;
        this.props.colorClass = "blue";
        this.props.iconClass = "mic";
        this.DEFAULT_COLOR_CLASS = "blue";
        this.applyProps(props);
        this.validateExpectedIntent(false);
    }

    setConfig(key, value) {
        const { inputNumberVariables } = window.apm.serialized.rootSettings.features;
        const {
            dtmfIntent,
            expectedIntent,
            localIntents = []
        } = this.props.config;
        const addLocalIntent = (intentId) => {
            if (!intentId || localIntents.includes(intentId)) {
                return false;
            }
            localIntents.push(intentId);
            this.props.config.localIntents = localIntents;
            return true;
        };

        if (key === "expectedIntent") {
            if (addLocalIntent(value)) {
                document.dispatchEvent(new CustomEvent("ev-property-item-list-refresh"));
            }
        } else if (key === "localIntents") {
            value ??= [];
            const itemsWereRemoved = localIntents.length > value.length;
            if (itemsWereRemoved) {
                // If the item deleted from local intents is the same intent assigned to expectedIntent remove intent as expectedIntent.
                if (expectedIntent && !value.includes(expectedIntent)) {
                    this.props.config.expectedIntent = "";
                }
                // If the item deleted from local intents is the same intent assigned to dtmfIntent remove intent as dtmfIntent.
                if (dtmfIntent && !value.includes(dtmfIntent)) {
                    this.props.config.dtmfIntent = "";
                }
                document.dispatchEvent(new CustomEvent("ev-property-refresh"));
            }
        } else if (key === "DTMF") {
            // If DTMF is marked as false, remove the intent from dtmfIntent.
            if (!value && this.props.config.dtmfIntent) {
                this.props.config.dtmfIntent = "";
            }
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        } else if (key === "inputType" && value === "YN_GENERIC") {
            const intents = window.apm.knowledgeBase.getIntents();
            const nIntent = intents.find((intent) => intent.name === "NO");
            const yIntent = intents.find((intent) => intent.name === "YES");

            addLocalIntent(nIntent.value);
            addLocalIntent(yIntent.value);
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
            document.dispatchEvent(new CustomEvent("ev-property-item-list-refresh"));
        }

        if (
            !inputNumberVariables
            && [
                "dtmfInterDigitTimeout",
                "lengthOfSilence",
                "maxIntentTimeout"
            ].includes(key)
        ) {
            value = safeParseInt(value) ?? "";
        }

        super.setConfig(key, value);
        this.validateExpectedIntent();
    }

    /**
     * @param {boolean} notifyViews refreshes property panel & canvas after construction.
     */
    validateExpectedIntent(notifyViews = true) {
        if (!window.apm?.knowledgeBase) {
            return;
        }
        const inputTypes = window.apm.knowledgeBase.getInputTypes();
        const inputType = inputTypes.find((it) => it.value === this.props.config.inputType);
        if (!inputType) {
            return;
        }

        const hasExpectedIntentError = inputType.required && !this.props.config.expectedIntent;
        const error = (!hasExpectedIntentError) ? null : `${window.l.property.expected_intent} is required`;

        this.setError(DesignErrorKey.expectedIntentError, error);
        if (!notifyViews) {
            return;
        }

        document.dispatchEvent(new CustomEvent("ev-property-refresh-error", {
            detail: {
                target: "expectedIntent",
                error
            }
        }));
        window.apm.postEventToCurrentView({ target: "request-refresh" });
    }

    getUnusedIntents() {
        const { serialized } = window.apm;
        const usedIntents = this.getLinks().reduce((prev, link) => {
            const condition = link.getConfig('condition');
            return prev.concat(condition.length ? condition.join() : []);
        }, []).join();
        const localIntentIds = this.getConfig('localIntents') || [];

        return serialized.components.intents.filter((intent) => {
            return (
                localIntentIds.includes(intent.id)
                && !usedIntents.includes(intent.name)
            );
        }).map((intent) => intent.id);
    }

    checkUnusedIntents() {
        const error = (this.getUnusedIntents().length < 1) ? null : (
            "Unused Local Intent"
        );
        this.setError(DesignErrorKey.validationError, error);
        window.apm.postEventToCurrentView({ target: "request-refresh" });
    }

    getConfigViews() {
        const { inputNumberVariables } = window.apm.serialized.rootSettings.features;
        this.checkUnusedIntents();
        const {
            disambiguationPromptId,
            noInputTimeoutOverride
        } = this.props.config;
        const propLabels = window.l.property;

        /** @type {(props: CreateNumberConfigViewProps) => unknown} props */
        const createNumberConfigView = ({
            name,
            label,
            tooltip
        }) => {
            const placeholder = propLabels.time_in_milliseconds;
            const fieldProps = {
                label,
                name,
                onChange: (newValue) => this.setConfigAndParent(name, newValue),
                placeholder,
                tooltip,
                value: this.props.config[name]
            };

            return (inputNumberVariables) ? (
                <ValueOrVariableAutocomplete {...fieldProps} valueType={VariableType.number} />
            ) : (
                {
                    type: "Input",
                    label,
                    key: name,
                    hint: placeholder,
                    tooltip
                }
            );
        };

        return [
            ...this.getConfigViewNameField(),
            {
                type: "Textarea",
                label: propLabels.description,
                key: "description"
            },
            {
                type: ConfigViewType.oldItemList,
                label: propLabels.recognition_variables,
                key: "inputs",
                view: ItemListView.argument
            },
            {
                type: ConfigViewType.oldItemList,
                label: propLabels.outputs,
                key: "outputs",
                view: ItemListView.return
            },
            {
                type: "Dropdown",
                key: "inputType",
                label: propLabels.input_type,
                optionName: propLabels.input_type_opt,
                options: window.apm.knowledgeBase.getInputTypes()
            },
            {
                type: "Switch",
                key: "sensitiveData",
                label: propLabels.sensitive_data,
                checked: false
            },
            {
                type: "Switch",
                key: "DTMF",
                label: propLabels.dtmf,
                checked: false
            },
            {
                type: "Switch",
                key: "dtmfLiveAgent",
                label: propLabels.dtmf_live_agent,
                checked: false
            },
            ...((!this.getConfig("DTMF")) ? [] : [
                {
                    type: "Switch",
                    key: "dtmfOnly",
                    label: propLabels.dtmf_only,
                    checked: false
                },
                // Adding removable: true, option to remove intent as expected intent.
                {
                    type: "DtmfDropDown",
                    key: "dtmfIntent",
                    label: propLabels.dtmf_intent,
                    removable: true,
                    optionName: propLabels.intent_opt,
                    tooltip: "Choose an intent from \"Intents handled locally\" list below."
                },
                createNumberConfigView({
                    label: propLabels.dtmfInterDigitTimeout,
                    name: "dtmfInterDigitTimeout",
                    tooltip: "How long the voice browser will wait between DTMF key presses before assuming someone is done"
                }),
                {
                    type: "Input",
                    label: propLabels.dtmfTerminationKey,
                    key: "dtmfTerminationKey",
                    hint: propLabels.typically_a_hash,
                    tooltip: "What key ends DTMF input."
                }
            ]),
            createNumberConfigView({
                label: propLabels.maxIntentTimeout,
                name: "maxIntentTimeout",
                tooltip: "The total length of an utterance that can possibly be given. Defaulted to 50000"
            }),
            createNumberConfigView({
                label: propLabels.lengthOfSilence,
                name: "lengthOfSilence",
                tooltip: "The time after caller stops talking that is required before end of speech is fired. Defaulted to 2000"
            }),
            (
                <div className="form-component-container">
                    <ObjectDropdown
                        className="form-component dropdown"
                        isClearable
                        label={propLabels.disambiguationPromptId}
                        name="disambiguationPromptId"
                        onChange={(newValue) => {
                            this.setConfigAndParent('disambiguationPromptId', newValue);
                        }}
                        tooltip={propLabels.disambiguationPromptIdTooltip}
                        type={ObjectClass.prompt}
                        useStateValue
                        value={disambiguationPromptId}
                    />
                </div>
            ),
            {
                content: (
                    <NoInputTimeoutOverride
                        onValueChange={(newValue) => {
                            this.setConfigAndParent('noInputTimeoutOverride', newValue);
                        }}
                        value={noInputTimeoutOverride}
                    />
                ),
                label: propLabels.noInputTimeout,
                tooltip: propLabels.noInputTimeout_tooltip,
                type: ConfigViewType.jsx,
            },
            {
                type: "IntentDropdown",
                key: "expectedIntent",
                label: propLabels.expected_intent,
                optionName: propLabels.intent_opt,
                removable: true,
                tooltip: "The intent that you would expect ASR to use when returning data to the application"
            },
            {
                type: "IntentDropdown",
                key: "impliedIntent",
                label: propLabels.implied_intent,
                optionName: propLabels.intent_opt,
                tooltip: "When checking compound intents for this input node, the implied intent will be added as if the user had given it too.",
                removable: true
            },
            {
                type: "Dropdown",
                key: "globalHandling",
                label: propLabels.global_handling,
                optionName: propLabels.global_handling_opt,
                options: [
                    { name: "All", value: "ALL" },
                    { name: "Conversational only", value: "CONVERSATIONAL_ONLY" }
                ]
            },
            {
                type: ConfigViewType.oldItemList,
                label: propLabels.local_intent,
                key: "localIntents",
                view: ItemListView.localIntent
            },
            {
                type: "Input",
                label: propLabels.analyst_class,
                key: "analystClass",
                suggestion: "variable"
            },
            {
                type: "Switch",
                key: "ignoreDroppableIntents",
                label: propLabels.ignore_droppable_intents,
                checked: false
            },
            {
                type: "Switch",
                key: "ignoreCompoundIntents",
                label: propLabels.ignore_compound_intents,
                checked: false
            },
            {
                type: "hr"
            },
            {
                type: "Textarea",
                label: propLabels.note,
                key: "note"
            }
        ];
    }
}
